import {
  type ShortCode,
  type EnrichmentContext,
  type TransformedItemPost,
  type TransformedItemContributor,
  fetchEmbeddedPosts,
  fetchEmbeddedPostsByCat,
  withDefaultContributors,
} from '@highereducation/wordpress';
import { ArticleCard, type ArticleCardProps } from '@highereducation/hen-ui';
import { parseShowImage } from '../utils';

interface ArticleCardShortcodeProps {
  'image-src': string;
  'image-alt'?: string;
  title: string;
  brow?: string;
  'brow-label'?: string;
  author?: string;
  date?: string;
  href?: string;
  size?: ArticleCardProps['size'];
  border?: ArticleCardProps['border'];
  layout?: 'stacked' | 'overlay';
  id?: string;
  category?: string;
  latest?: string;
  order?: string;
  'hover-style'?: 'none' | 'shadow';
  /** Shortcode parsers often pass strings (`"false"`, `"true"`). Omitted = show image. */
  'show-image'?: string | boolean;
  /** Set to `"false"` to hide the category brow text. Omitted = show brow. */
  'show-brow'?: string | boolean;
  [key: string]: unknown;
}

/**
 * ArticleCardShortcode - Shortcode wrapper for ArticleCard composite component
 *
 * Displays an article card with image, title, and optional metadata.
 * Supports multiple size and border variants.
 *
 * **`layout="overlay"`** uses the same CMS/static fields and the same `getEnrichedData` path as the default stacked card; only the visual layout (full-bleed image + overlay copy) changes.
 *
 * **Note:** className is intentionally excluded from shortcode props as per design system guidelines.
 *
 * Supported shortcode attributes:
 * - image-src: string (required without dynamic attrs) - Image source URL
 * - image-alt: string (optional) - Alt text for the image
 * - title: string (required without dynamic attrs) - Article title
 * - brow: string (optional) - Eyebrow above title; overrides CMS primary category when both exist
 * - brow-label: string (optional) - Alias for brow; takes priority when both are present
 * - author: string (optional) - Author name (displayed as "By {author}")
 * - date: string (optional) - Publication date string
 * - href: string (optional) - Link URL for the card
 * - size: 'lg' | 'md' | 'sm' | 'compact' (optional, default: 'lg') - Size variant
 * - border: 'none' | 'padding' | 'stroke' | 'shadow' (optional, default: 'none') - Border variant
 * - layout: 'stacked' | 'overlay' (optional, default: stacked) - overlay = full-bleed image tile with bottom gradient (Figma Article Row)
 * - hover-style: 'none' | 'shadow' (optional, default: none) - shadow on hover (stacked layout only)
 * - show-image: 'true' | 'false' (optional, default: true) - set false to hide image while keeping image URL from CMS/static attrs
 *
 * Dynamic attributes (fetches a single article from CMS):
 * - id: string - Post ID to fetch
 * - category: string - Comma-separated category slugs (AND logic, returns latest match)
 * - latest: string - When truthy (e.g. "true"), fetches the single most recent post globally
 * - order: 'updated' | 'published' | 'custom' - Sort order when using category or latest
 *
 * Priority: id > category > latest.
 * Explicit shortcode attrs (title, image-src, href, date, image-alt) always override CMS-fetched values for those fields.
 * Layout attrs (size, border, brow/brow-label, show-brow, layout, hover-style, show-image) always come from the shortcode (no CMS equivalent).
 *
 * @example Dynamic - Fetch by ID:
 * [hen-ui-article-card id="901" size="lg" border="stroke"][/hen-ui-article-card]
 *
 * @example Dynamic - Latest from category:
 * [hen-ui-article-card category="psychology" order="published" size="md"][/hen-ui-article-card]
 *
 * @example Dynamic - Latest post globally:
 * [hen-ui-article-card latest="true" size="lg" border="stroke"][/hen-ui-article-card]
 *
 * @example Static (unchanged):
 * [hen-ui-article-card image-src="/img.jpg" title="My Article" href="/article"][/hen-ui-article-card]
 */
const parseTruthy = (val?: string): boolean =>
  ['true', '1', 'yes'].includes((val ?? '').toLowerCase().trim());

async function enrichPostWithDefaultContributor(
  post: TransformedItemPost | undefined,
  config: NonNullable<EnrichmentContext['config']>,
): Promise<TransformedItemPost | null> {
  if (!post) return null;
  const [enriched] = await withDefaultContributors([post], config);
  return enriched ?? null;
}

export const ArticleCardShortcode: ShortCode<
  ArticleCardShortcodeProps,
  TransformedItemPost | null
> = {
  getEnrichedData: async (
    _content: string,
    context?: EnrichmentContext,
  ): Promise<TransformedItemPost | null> => {
    const attrs = context?.attrs ?? {};
    const config = context?.config;

    if (!config?.cmsUrl || !config?.postTypeGql) return null;

    const { cmsUrl, postTypeGql } = config;
    const id = attrs['id'];
    const category = attrs['category'];
    const latest = attrs['latest'];
    const order =
      (attrs['order'] as 'updated' | 'published' | 'custom') || undefined;

    if (id) {
      const trimmedId = id.trim();
      if (!trimmedId) return null;
      const result = await fetchEmbeddedPosts([trimmedId], config);
      return enrichPostWithDefaultContributor(result?.[trimmedId], config);
    }

    if (category) {
      const categories = category
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean);
      if (!categories.length) return null;
      const result = await fetchEmbeddedPostsByCat(
        categories,
        { limit: '1', orderBy: order },
        postTypeGql,
        cmsUrl,
        config.fallbackPostImage,
      );
      return enrichPostWithDefaultContributor(result?.[0], config);
    }

    if (parseTruthy(latest)) {
      const result = await fetchEmbeddedPostsByCat(
        [],
        { limit: '1', orderBy: order },
        postTypeGql,
        cmsUrl,
        ...(config.fallbackPostImage ? [config.fallbackPostImage] : []),
      );
      return enrichPostWithDefaultContributor(result?.[0], config);
    }

    return null;
  },

  component: ({
    'image-src': imageSrc,
    'image-alt': imageAlt,
    title,
    brow,
    'brow-label': browLabelAttr,
    author,
    date,
    href,
    size = 'lg',
    border = 'none',
    'hover-style': hoverStyleAttr = 'none',
    layout = 'stacked',
    'show-image': showImageAttr,
    'show-brow': showBrowAttr,
    enrichments,
  }) => {
    const post = enrichments?.data as TransformedItemPost | null | undefined;
    const cmsAuthor = post?.contributors?.[0] as
      | TransformedItemContributor
      | undefined;

    const image = imageSrc || post?.image || '';
    const alt =
      imageAlt || post?.imagealt || title || post?.title || 'Article image';
    const heading = title || post?.title || '';
    const link = href || post?.link;
    const articleDate = date || post?.date;
    const authorName = author || cmsAuthor?.name;
    const showBrow = parseShowImage(showBrowAttr);
    const browValue = showBrow ? brow || post?.brow : undefined;
    const browLabel = showBrow ? browLabelAttr : undefined;
    const cardLayout = layout === 'overlay' ? 'overlay' : 'stacked';
    const cardHoverStyle = hoverStyleAttr === 'shadow' ? 'shadow' : 'none';
    const showImage = parseShowImage(showImageAttr);
    return (
      <ArticleCard
        image={image}
        imageAlt={alt}
        title={heading}
        brow={browValue}
        browLabel={browLabel}
        author={authorName}
        date={articleDate}
        href={link}
        size={size}
        border={border}
        layout={cardLayout}
        showImage={showImage}
        hoverStyle={cardHoverStyle}
      />
    );
  },
};
